import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import InvestorDashboard from './components/InvestorDashboard';
import InvestorSetupPassword from './components/InvestorSetupPassword';
import { ThemeProvider } from './context/ThemeContext';
import { getToken, getUser, setAuth, clearAuth, getApiUrl } from './utils/auth';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = getToken();
    const savedUser = getUser();

    if (token && savedUser) {
      // Verify token with backend
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.user);
        setIsAuthenticated(true);
        setAuth(data.user, token);
      } else {
        // Token invalid, clear auth
        clearAuth();
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Token verification error:', error);
      clearAuth();
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    setAuth(userData, token);
  };

  const handleLogout = () => {
    clearAuth();
    setUser(null);
    setIsAuthenticated(false);
  };

  // Investor setup password page (no auth; URL: /investor/setup-password?token=...)
  const isInvestorSetupPassword = window.location.pathname === '/investor/setup-password';
  if (isInvestorSetupPassword) {
    return (
      <ThemeProvider>
        <div className="App">
          <InvestorSetupPassword />
        </div>
      </ThemeProvider>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center transition-colors duration-200">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-primary-600 dark:text-primary-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600 dark:text-dark-muted">Loading...</p>
        </div>
      </div>
    );
  }

  const isInvestor = user?.type === 'investor';

  return (
    <ThemeProvider>
      <div className="App">
        {isAuthenticated ? (
          isInvestor ? (
            <InvestorDashboard user={user} onLogout={handleLogout} />
          ) : (
            <Dashboard user={user} onLogout={handleLogout} />
          )
        ) : (
          <Login onLogin={handleLogin} />
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
